import { useState } from 'react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Two-step R2 upload:
 *   1. Backend issues presigned PUT URL.
 *   2. Browser PUTs file bytes directly to R2 with onProgress callback.
 * Returns the R2 object key once upload completes.
 */
export function useR2Upload(token) {
  const [progress, setProgress] = useState(0);
  const [uploading, setUploading] = useState(false);

  const upload = async (file, kind /* 'video' | 'thumbnail' */) => {
    setProgress(0);
    setUploading(true);
    try {
      const presignRes = await fetch(`${API}/api/videos/presign`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          filename: file.name,
          content_type: file.type || 'application/octet-stream',
          kind,
        }),
      });

      if (!presignRes.ok) {
        const err = await presignRes.json().catch(() => ({}));
        throw new Error(err.detail || `Failed to get upload URL (${presignRes.status})`);
      }

      const { upload_url, key } = await presignRes.json();

      // Direct browser → R2 PUT with progress tracking
      await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', upload_url);
        xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) {
            setProgress(Math.round((e.loaded / e.total) * 100));
          }
        };
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) resolve();
          else reject(new Error(`R2 upload failed: HTTP ${xhr.status}`));
        };
        xhr.onerror = () => reject(new Error('R2 upload network error'));
        xhr.send(file);
      });

      return key;
    } finally {
      setUploading(false);
    }
  };

  return { upload, progress, uploading };
}
